// Helpers that turn the derived-rateplan applicable-rates payload into the
// three shapes the campaign UI needs: the info-banner bullet list, the
// applicable room-code × rate-plan-code combos that seed the Rooms & Rate
// Plans grid, and the create-promo request body sent on launch.
//
// BE response shape:
//   {
//     propertyId,
//     stackingCombinations: [
//       {
//         stackingPromoDescription,   // "HTPKG => Summer Sale(5%)"
//         baseRateplanId, baseRateplanCode,
//         derivedFrom, derivedFromType,
//         roomtypeIds: [...], roomtypeCodes: [...]
//       }
//     ]
//   }
//
// Every combination the BE returns is already eligible — there is no status
// or type filtering to do on this side.

import { DISTRIBUTION } from "../../../../pages/property-config/rate-plans-derived-rate/constants";

const codeById = (entities) =>
  new Map(
    (entities || [])
      .filter((e) => e?.id != null && e?.code)
      .map((e) => [e.id, e.code])
  );

// Unwraps the AppResponse envelope down to the combinations array. Anything
// unexpected collapses to [] so the banner and grid stay empty rather than
// throwing.
export const extractStackingCombinations = (res) => {
  const combinations = res?.data?.data?.stackingCombinations;
  return Array.isArray(combinations) ? combinations : [];
};

const collectComboKeys = (item, rpCodeById, roomCodeById, seen, combos) => {
  // Match by baseRateplanId: the property's rate-plan grid columns come from
  // getRatePlansByProperty which returns base rate plans, so derivedFrom
  // (a derived-rate id) would not resolve to a column.
  const rateplanId = item?.baseRateplanId;
  if (rateplanId == null) return;
  const mLeft = rpCodeById.get(rateplanId);
  if (!mLeft) return;

  const roomtypeIds = Array.isArray(item.roomtypeIds) ? item.roomtypeIds : [];
  for (const roomtypeId of roomtypeIds) {
    const mRight = roomCodeById.get(roomtypeId);
    if (!mRight) continue;
    const key = `${rateplanId}::${roomtypeId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    combos.push({ mRight, mLeft, rateplanId, roomtypeId });
  }
};

// One banner line per combination: the chain breadcrumb the BE rendered,
// e.g. "HTPKG => Summer Sale(5%)". A combination that stacks straight on the
// base rateplan carries just its code ("HTPKG") and is still a valid line.
export const buildStackingBannerList = (list) => {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const item of list) {
    const description = item?.stackingPromoDescription;
    if (!description) continue;
    if (seen.has(description)) continue;
    seen.add(description);
    out.push(description);
  }
  return out;
};

// Builds the request body for BE/api/v1/derived-rateplan/get-applicable-rates
// from the parsed campaign object plus the property/recommendation context.
// Returns null when any required field is missing so callers can skip the
// fetch until every dependency has resolved.
const toId = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const findValidRange = (ranges) =>
  (Array.isArray(ranges) ? ranges : []).find((r) => r?.startDate && r?.endDate);

export const buildApplicableRatesPayload = ({
  campaign,
  propertyId,
  clientId,
} = {}) => {
  if (!campaign) return null;
  const r = campaign.restrictionConfig || {};
  const bookRange = findValidRange(r.bookDates);
  const stayRange = findValidRange(r.stayDates);
  const pid = toId(propertyId ?? campaign.propertyId);
  // clientId is always sourced from the recommendation (ownerId) at the
  // callsite — the parsed campaign object doesn't carry it, so no fallback.
  const cid = toId(clientId);
  if (pid == null || cid == null || !bookRange || !stayRange) return null;
  return {
    propertyId: pid,
    clientId: cid,
    bookingStartDate: bookRange.startDate,
    bookingEndDate: bookRange.endDate,
    stayStartDate: stayRange.startDate,
    stayEndDate: stayRange.endDate,
  };
};

// Builds the create-promo body posted to METADATA/v2/metadata/derived-rateplan/promo
// when a campaign is launched. Each stacking combination becomes one entry in
// rateRoomSelections[0].combinations verbatim, minus the banner-only
// description. restrictionConfig and distributionConfig are dropped when they
// have nothing to say rather than sent empty.
export const buildCampaignPromoPayload = ({
  stackingCombinations,
  propertyId,
  campaignId,
  name,
  priceConfig,
  restrictionConfig,
  clientId,
} = {}) => {
  const combinations = (
    Array.isArray(stackingCombinations) ? stackingCombinations : []
  ).map((row) => ({
    baseRateplanId: row.baseRateplanId,
    baseRateplanCode: row.baseRateplanCode,
    derivedFrom: row.derivedFrom,
    derivedFromType: row.derivedFromType,
    roomtypeIds: row.roomtypeIds,
    roomtypeCodes: row.roomtypeCodes,
  }));

  const payload = {
    name,
    campaignId,
    status: "ACTIVE",
    rateRoomSelections: [{ propertyId, combinations }],
    priceConfig,
    contractConfig: {
      mealPlanCode: null,
      cancellationPolicy: { windows: [] },
    },
    commercialModel: "COMMUNITY",
    promoType: "BUYER_CAMPAIGN",
  };

  if (restrictionConfig && Object.keys(restrictionConfig).length > 0) {
    payload.restrictionConfig = restrictionConfig;
  }
  if (clientId != null) {
    // distribution must be EXCLUSIVE on a BUYER_CAMPAIGN promo — metadata rejects
    // the create otherwise, and an absent value fails that check the same as a
    // wrong one.
    payload.distributionConfig = {
      distribution: DISTRIBUTION.EXCLUSIVE,
      isExclusive: true,
      clientIds: [clientId],
    };
  }
  return payload;
};

export const buildApplicableCombos = (list, rooms, ratePlans) => {
  if (
    !Array.isArray(list) ||
    !Array.isArray(rooms) ||
    !Array.isArray(ratePlans) ||
    rooms.length === 0 ||
    ratePlans.length === 0
  ) {
    return [];
  }

  const roomCodeById = codeById(rooms);
  const rpCodeById = codeById(ratePlans);

  const seen = new Set();
  const combos = [];
  for (const item of list) {
    collectComboKeys(item, rpCodeById, roomCodeById, seen, combos);
  }
  return combos;
};
